import { useEffect, useRef, useState } from 'react';
import { StatusBadge } from '@argink/ui';
import { PARTICIPANT_STATUS_LABEL_OWNER } from '@argink/shared/surface-lexicon';

export type StageState = 'tracking' | 'signer' | 'methods';

/**
 * O produto que ATRAVESSA os momentos narrativos.
 *
 * O mandato é explícito: a UI deve mudar de estado ao longo da narrativa em vez
 * de cada assunto ganhar um mock novo. Por isso existe uma superfície só, fixa
 * na coluna direita, que troca de tela conforme o texto avança:
 *
 *   acompanhar  → a lista de participantes de um envio
 *   assinar     → a mesma jornada pelo lado de quem recebe
 *   métodos     → a escolha de como o documento precisa ser assinado
 *
 * Cada estado é uma tela que o produto realmente tem. Nada aqui anuncia recurso
 * inexistente.
 *
 * Não há barra de "semáforo" no topo: os três pontinhos de janela de browser são
 * o clichê que faz uma superfície ler como MOCKUP em vez de produto, e o §6 do
 * contrato os proíbe explicitamente. A tela já tem cabeçalho próprio.
 */

const TRACKING = [
  { name: 'Helena Braga', status: 'signed' as const, detail: 'Assinou terça, 16:02' },
  { name: 'Paulo Nogueira', status: 'declined' as const, detail: 'Recusou, valores divergentes' },
  { name: 'Sérgio Lima', status: 'notified' as const, detail: 'Convite enviado há 6 dias' },
];

function toneFor(status: keyof typeof PARTICIPANT_STATUS_LABEL_OWNER) {
  if (status === 'signed') return 'proof' as const;
  if (status === 'declined') return 'danger' as const;
  return 'neutral' as const;
}

export function StageProduct({ state }: { state: StageState }) {
  return (
    <div className="stage-screen" data-state={state}>
      {state === 'tracking' ? (
        <div className="stage-view">
          <div className="stage-view__head">
            <div>
              <p className="stage-view__eyebrow">Envio</p>
              <p className="stage-view__title">Aditivo contratual, Fornecedor Norte</p>
            </div>
            <StatusBadge tone="info">Aguardando assinatura</StatusBadge>
          </div>
          <p className="stage-view__meta">1 de 3 assinaram. Prazo termina amanhã.</p>
          <ul className="stage-list">
            {TRACKING.map((p) => (
              <li key={p.name}>
                <span>
                  <strong>{p.name}</strong>
                  <span>{p.detail}</span>
                </span>
                <StatusBadge tone={toneFor(p.status)}>
                  {PARTICIPANT_STATUS_LABEL_OWNER[p.status]}
                </StatusBadge>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {state === 'signer' ? (
        <div className="stage-view stage-view--signer">
          <div className="stage-phone">
            <p className="stage-view__eyebrow">Documento recebido</p>
            <p className="stage-view__title">Contrato de prestação de serviços</p>
            <div className="stage-phone__sheet" aria-hidden="true">
              <span className="is-title" />
              <span />
              <span />
              <span className="is-short" />
              <span />
              <span className="is-short" />
            </div>
            <p className="stage-phone__consent">Li o documento e concordo em assiná-lo.</p>
            {/* Representação, não controle: um botão real prometeria uma ação que
              * esta página não executa. Sem foco, sem papel de botão. */}
            <span className="stage-phone__cta" role="presentation">
              Assinar
            </span>
            <p className="stage-phone__note">Sem criar conta. Sem senha.</p>
          </div>
        </div>
      ) : null}

      {state === 'methods' ? (
        <div className="stage-view">
          <div className="stage-view__head">
            <div>
              <p className="stage-view__eyebrow">Novo envio</p>
              <p className="stage-view__title">Como este documento será assinado</p>
            </div>
          </div>
          <div className="stage-method is-selected">
            <span className="stage-method__mark" aria-hidden="true" />
            <span>
              <strong>Assinatura eletrônica</strong>
              <span>Identificação do signatário e registro de evidências.</span>
            </span>
          </div>
          <div className="stage-method">
            <span className="stage-method__mark" aria-hidden="true" />
            <span>
              <strong>Certificado digital ICP-Brasil</strong>
              <span>Assinatura qualificada, com o certificado do próprio signatário.</span>
            </span>
          </div>
          <p className="stage-view__meta">A escolha vale por envio.</p>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Qual momento está em foco.
 *
 * `IntersectionObserver` em vez de cálculo de scroll: o navegador já sabe
 * responder isso, e ler `scrollY` a cada quadro custaria layout em toda rolagem.
 * Sem observer disponível (jsdom nos testes), fica no primeiro estado, que é uma
 * degradação honesta: a tela continua correta, só não troca.
 */
export function useStageState(ids: StageState[]): [StageState, (el: HTMLElement | null, i: number) => void] {
  const [active, setActive] = useState<StageState>(ids[0]);
  const nodes = useRef<(HTMLElement | null)[]>([]);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (!visible) return;
        const index = nodes.current.indexOf(visible.target as HTMLElement);
        if (index >= 0) setActive(ids[index]);
      },
      { rootMargin: '-45% 0px -45% 0px', threshold: [0, 0.25, 0.5, 1] },
    );
    for (const node of nodes.current) if (node) observer.observe(node);
    return () => observer.disconnect();
    // `ids` é uma constante de módulo no consumidor; observar de novo a cada
    // render remontaria o observer sem motivo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return [active, (el, i) => { nodes.current[i] = el; }];
}
